import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

function UserInfo({ setPetInfo, petInfo, setIsComplete }) {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState({
    name: petInfo.userInfo?.name || "",
    surname: petInfo.userInfo?.surname || "",
    email: petInfo.userInfo?.email || "",
    phone: petInfo.userInfo?.phone || "",
    contactPermission: petInfo.userInfo?.contactPermission || false,
    privacyPolicyAccepted: petInfo.userInfo?.privacyPolicyAccepted || false
  });

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const validatePhone = (phone) => {
    return phone.length >= 10;
  };

  const handleContactPermissionChange = (e) => {
    const newValue = e.target.checked;
    setUserInfo({ ...userInfo, contactPermission: newValue });
    setPetInfo({
      ...petInfo,
      userInfo: { ...userInfo, contactPermission: newValue }
    });
  };

  const handlePrivacyPolicyChange = (e) => {
    const newValue = e.target.checked;
    setUserInfo({ ...userInfo, privacyPolicyAccepted: newValue });
    setPetInfo({
      ...petInfo,
      userInfo: { ...userInfo, privacyPolicyAccepted: newValue }
    });
  };

  useEffect(() => {
    // Email zorunlu, telefon isteğe bağlı
    const isEmailValid = validateEmail(userInfo.email);

    if (isEmailValid && userInfo.privacyPolicyAccepted) {
      setIsComplete(true);
    } else {
      setIsComplete(false);
    }

    setPetInfo({
      ...petInfo,
      userInfo: userInfo,
    });
  }, [userInfo]);

  return (
    <div className="flex-col items-start justify-start h-full relative w-full max-w-lg px-4 sm:px-0 mb-12">
      <div className="pt-0">
        <div className="font-poppins text-2xl text-yellow-700 font-bold">
          {petInfo.name ? `${petInfo.name} ${t("nutritionReportFor")}` : t("nutritionReport")}
        </div>
        <div className="font-poppins text-yellow-600 p-2">
          {t("nutritionReportDesc")}
        </div>
        <div className="font-poppins text-sm text-kito-green p-3 bg-green-50 rounded-xl mt-3 border border-green-200">
          <span className="font-semibold">{t("reportContents")}</span>
          <ul className="mt-2 text-xs space-y-1 text-green-700">
            <li>• {t("dailyCalorieAndGram")}</li>
            <li>• {t("freshDryComparison")}</li>
            <li>• {t("nutritionRecommendations")}</li>
          </ul>
        </div>
      </div>

      <div className="mt-6 justify-start items-start flex flex-col w-full">
        <div className="relative z-0 w-full mb-6 group">
          <input
            onChange={(e) => {
              setUserInfo({ ...userInfo, name: e.target.value });
            }}
            type="text"
            name="userName"
            className="block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 border-yellow-400 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-kito-green focus:outline-none focus:ring-0 focus:border-kito-green peer"
            placeholder=" "
            value={userInfo.name}
          />
          <label
            htmlFor="userName"
            className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-focus:dark:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {t("name")} <span className="text-gray-400 text-xs">({t("optional")})</span>
          </label>
        </div>

        <div className="relative z-0 w-full mb-6 group">
          <input
            onChange={(e) => {
              setUserInfo({ ...userInfo, surname: e.target.value });
            }}
            type="text"
            name="userSurname"
            className="block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 border-yellow-400 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-kito-green focus:outline-none focus:ring-0 focus:border-kito-green peer"
            placeholder=" "
            value={userInfo.surname}
          />
          <label
            htmlFor="userSurname"
            className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-focus:dark:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {t("surname")} <span className="text-gray-400 text-xs">({t("optional")})</span>
          </label>
        </div>

        <div className="relative z-0 w-full mb-6 group">
          <input
            onChange={(e) => {
              setUserInfo({ ...userInfo, email: e.target.value });
            }}
            type="email"
            name="userEmail"
            className={`block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 ${userInfo.email && !validateEmail(userInfo.email)
              ? "border-red-500"
              : "border-yellow-400"
              } appearance-none dark:text-white dark:border-gray-600 dark:focus:border-kito-green focus:outline-none focus:ring-0 focus:border-kito-green peer`}
            placeholder=" "
            value={userInfo.email}
            required
          />
          <label
            htmlFor="userEmail"
            className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-focus:dark:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {t("email")} <span className="text-red-500">*</span>
          </label>
          {userInfo.email && !validateEmail(userInfo.email) && (
            <p className="text-red-500 text-xs mt-1">{t("invalidEmail")}</p>
          )}
        </div>

        <div className="relative z-0 w-full mb-6 group">
          <input
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              setUserInfo({ ...userInfo, phone: value });
            }}
            type="tel"
            name="userPhone"
            className={`block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 ${userInfo.phone && !validatePhone(userInfo.phone)
              ? "border-red-500"
              : "border-yellow-400"
              } appearance-none dark:text-white dark:border-gray-600 dark:focus:border-kito-green focus:outline-none focus:ring-0 focus:border-kito-green peer`}
            placeholder=" "
            value={userInfo.phone}
          />
          <label
            htmlFor="userPhone"
            className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-focus:dark:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {t("phone")} <span className="text-gray-400 text-xs">({t("optional")})</span>
          </label>
          {userInfo.phone && !validatePhone(userInfo.phone) && (
            <p className="text-red-500 text-xs mt-1">{t("invalidPhone")}</p>
          )}
        </div>

        <div className="relative z-0 w-full mb-6 group flex items-start text-left">
          <div
            className={`flex items-center justify-center w-4 h-4 border rounded cursor-pointer transition-colors duration-200 mt-1 flex-shrink-0 ${userInfo.privacyPolicyAccepted ? 'bg-kito-green border-kito-green' : 'bg-white border-gray-300 hover:border-kito-green'}`}
            onClick={(e) => {
              const newValue = !userInfo.privacyPolicyAccepted;
              const simulatedEvent = { target: { checked: newValue } };
              handlePrivacyPolicyChange(simulatedEvent);
            }}
          >
            {userInfo.privacyPolicyAccepted && (
              <svg className="w-3 h-3 text-white" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.707 4.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L6 8.586l5.293-5.293a1 1 0 0 1 1.414 0z" fill="currentColor" />
              </svg>
            )}
          </div>
          <label
            className="ml-3 text-xs font-medium text-yellow-600 dark:text-gray-300 cursor-pointer select-none leading-relaxed"
            onClick={(e) => {
              const newValue = !userInfo.privacyPolicyAccepted;
              const simulatedEvent = { target: { checked: newValue } };
              handlePrivacyPolicyChange(simulatedEvent);
            }}
          >
            <span className="text-red-500">*</span> {t("privacyPolicy")}
          </label>
        </div>

        <div className="relative z-0 w-full mb-6 group flex items-start text-left">
          <div
            className={`flex items-center justify-center w-4 h-4 border rounded cursor-pointer transition-colors duration-200 mt-1 flex-shrink-0 ${userInfo.contactPermission ? 'bg-kito-green border-kito-green' : 'bg-white border-gray-300 hover:border-kito-green'}`}
            onClick={(e) => {
              const newValue = !userInfo.contactPermission;
              const simulatedEvent = { target: { checked: newValue } };
              handleContactPermissionChange(simulatedEvent);
            }}
          >
            {userInfo.contactPermission && (
              <svg className="w-3 h-3 text-white" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.707 4.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L6 8.586l5.293-5.293a1 1 0 0 1 1.414 0z" fill="currentColor" />
              </svg>
            )}
          </div>
          <label
            className="ml-3 text-xs font-medium text-yellow-600 dark:text-gray-300 cursor-pointer select-none leading-relaxed"
            onClick={(e) => {
              const newValue = !userInfo.contactPermission;
              const simulatedEvent = { target: { checked: newValue } };
              handleContactPermissionChange(simulatedEvent);
            }}
          >
            {t("contactPermission")}
          </label>
        </div>
      </div>
    </div>
  );
}

export default UserInfo; 